/**
 * Schema Store · 架构模型（领域层，无 UI）
 * ------------------------------------------------------------
 * 原型对应：DOMAINS / APPS / ARCDATA / MODEL
 * 唯一架构事实来源。前端不得硬编码任何域/应用/节点名称。
 * 数据经 repository（L0）获取 —— M1 来自 mock，M2 换后端签名不变。
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { archRepo } from '@/data/api/architecture-repository'

export interface Domain { code: string; name: string; desc?: string }
export interface AppGroup { code: string; name: string; dom: string }
export interface App { code: string; name: string; dom: string; grp: string; [k: string]: any }
export interface BizNode { code: string; rp: string; name: string; [k: string]: any }

export function createSchemaState(source: Pick<typeof archRepo, 'indexes'> = archRepo) {
  const loaded = ref(false)
  const domains = ref<any[]>([])
  const grps = ref<any[]>([])
  const apps = ref<any[]>([])
  const nds = ref<any[]>([])
  const bcs = ref<any[]>([])
  const rps = ref<any[]>([])
  const eps = ref<any[]>([])
  const dobs = ref<any[]>([])
  const stat = ref<any>(null)
  const appBy = ref<Record<string, any>>({})
  const domBy = ref<Record<string, any>>({})
  const grpBy = ref<Record<string, any>>({})
  const appsByGrp = ref<Record<string, any[]>>({})
  const grpsByDom = ref<Record<string, any[]>>({})
  const abbs = ref<Record<string, any[]>>({})   // appCode → [{code,name,subs}]
  const rels = ref<any[]>([])                    // 对象间关系

  async function load() {
    // 支持增量刷新（不传 loaded guard，允许 CRUD 后重新拉取）
    const idx = await source.indexes()
    domains.value = idx.domains; grps.value = idx.grps; apps.value = idx.apps
    nds.value = idx.nds; bcs.value = idx.bcs; rps.value = idx.rps
    eps.value = idx.eps; dobs.value = idx.dobs; stat.value = idx.stat
    appBy.value = idx.appBy; domBy.value = idx.domBy; grpBy.value = idx.grpBy
    appsByGrp.value = idx.appsByGrp; grpsByDom.value = idx.grpsByDom
    abbs.value = idx.abbs || {}; rels.value = idx.rels || []
    loaded.value = true
  }

  const domainCount = computed(() => domains.value.length)
  const appCount = computed(() => apps.value.length)
  const ndCount = computed(() => nds.value.length)
  const bcCount = computed(() => bcs.value.length)
  const rpCount = computed(() => rps.value.length)

  /** 获取某应用的 L4 构建块数组（含 CRUD 后增量） */
  function abbsOf(appCode: string): any[] {
    return abbs.value[appCode] || []
  }

  return {
    loaded, domains, grps, apps, nds, bcs, rps, eps, dobs, stat,
    appBy, domBy, grpBy, appsByGrp, grpsByDom, abbs, rels,
    domainCount, appCount, ndCount, bcCount, rpCount,
    abbsOf, load
  }
}
export const useSchemaStore = defineStore('schema', () => createSchemaState())
